/**
 * Rotas do Watchdog (`api/v2/watchdog/*`, decisões 187 a 194 e 244 a 248) e das preferências da
 * conta (`api/v2/account/preferences`, decisão 195).
 *
 * Sem a rota no ar (404) ou com a rede fora, quem chama trata como "Watchdog desligado": o painel
 * publica sozinho a cada push no `main` e não pode depender do backend já ter a rota.
 */
import { api } from '../lib/apiClient';
import { deviceClock, deviceDetails, getDeviceId } from '../lib/watchdog';
import type { ApiResult } from '../types/api';

/** Detalhes para reconhecer o computador (decisão 247). Opcionais: o backend antigo não manda. */
export interface WatchdogDetails {
  browser: string | null;
  os: string | null;
  /** "141". */
  browserVersion?: string | null;
  /** "15", "11" (Windows 11) ou "10.15". */
  osVersion?: string | null;
  screen?: { width: number; height: number } | null;
}

export interface WatchdogDevice extends WatchdogDetails {
  id: string;
  authorizedAt: string | null;
  lastSeenAt: string | null;
  /** Este computador. */
  current: boolean;
  /** Com a vez: é o computador que está usando a licença. */
  active: boolean;
  /** Com a vez e com sinal recente. */
  inUse: boolean;
}

export interface WatchdogRequest extends WatchdogDetails {
  id: string;
  status: 'pending' | 'approved' | 'refused' | 'expired';
  createdAt: string | null;
  expiresAt: string | null;
}

/**
 * Troca em espera (decisão 245): a licença já trocou de computador hoje e só pode vir para cá
 * `waitHours` depois do último uso que gera leads em `lastUseDevice`.
 */
export interface WatchdogWait {
  availableAt?: string;
  waitHours?: number;
  lastUseDevice?: WatchdogDevice | null;
}

export type WatchdogSession =
  | { state: 'off' }
  | { state: 'active'; token?: string; device: WatchdogDevice; pending: WatchdogRequest[] }
  | ({
      state: 'paused';
      token?: string;
      device: WatchdogDevice;
      activeDevice: WatchdogDevice | null;
    } & WatchdogWait)
  | {
      state: 'authorize';
      request: WatchdogRequest;
      devices: WatchdogDevice[];
      emailSent?: boolean;
    }
  | { state: 'refused'; devices: WatchdogDevice[] };

export type WatchdogStatus =
  | { state: 'off' }
  | { state: 'device_required' }
  | { state: 'active'; pending: WatchdogRequest[] }
  | ({ state: 'paused'; activeDevice: WatchdogDevice | null } & WatchdogWait);

export type WatchdogPoll =
  | { status: 'off' | 'pending' | 'refused' | 'expired' }
  | { status: 'approved'; token?: string; device?: WatchdogDevice };

export interface WatchdogDevices {
  devices: WatchdogDevice[];
  pending: WatchdogRequest[];
}

const BASE = 'api/v2/watchdog';

/**
 * O identificador vai no cabeçalho (apiClient); o fingerprint, como sinal auxiliar. No corpo vão o
 * fuso e os idiomas do navegador (o email do pedido sai com eles, e o dia da regra das trocas é o
 * deste computador) e os detalhes do computador (`client`, decisão 247).
 */
export async function openSession(fingerprint?: string): Promise<ApiResult<WatchdogSession>> {
  getDeviceId();
  return api.post<WatchdogSession>(`${BASE}/session`, {
    ...(fingerprint ? { fingerprint } : {}),
    ...deviceClock(),
    client: await deviceDetails(),
  });
}

export async function requestAgain(): Promise<ApiResult<WatchdogSession>> {
  return api.post<WatchdogSession>(`${BASE}/requests`, {
    ...deviceClock(),
    client: await deviceDetails(),
  });
}

export function pollRequest(id: string): Promise<ApiResult<WatchdogPoll>> {
  return api.get<WatchdogPoll>(`${BASE}/requests/${encodeURIComponent(id)}`);
}

export function fetchStatus(): Promise<ApiResult<WatchdogStatus>> {
  return api.get<WatchdogStatus>(`${BASE}/status`);
}

export function claimTurn(): Promise<ApiResult<WatchdogSession>> {
  return api.post<WatchdogSession>(`${BASE}/claim`, {});
}

export function fetchDevices(): Promise<ApiResult<WatchdogDevices>> {
  return api.get<WatchdogDevices>(`${BASE}/devices`);
}

export function decideRequest(
  id: string,
  decision: 'approve' | 'refuse',
): Promise<ApiResult<WatchdogDevices>> {
  return api.post<WatchdogDevices>(`${BASE}/requests/${encodeURIComponent(id)}/${decision}`, {});
}

export function removeDevice(id: string): Promise<ApiResult<WatchdogDevices>> {
  return api.del<WatchdogDevices>(`${BASE}/devices/${encodeURIComponent(id)}`);
}

export interface WatchdogEmailView {
  request: WatchdogRequest;
  source: string | null;
}

/** Link do email: sem login, o token do link é a credencial. */
export function fetchEmailRequest(token: string): Promise<ApiResult<WatchdogEmailView>> {
  return api.get<WatchdogEmailView>(`${BASE}/email/${encodeURIComponent(token)}`);
}

export function decideEmailRequest(
  token: string,
  decision: 'approve' | 'refuse',
): Promise<ApiResult<{ request: WatchdogRequest }>> {
  return api.post<{ request: WatchdogRequest }>(
    `${BASE}/email/${encodeURIComponent(token)}/${decision}`,
    {},
  );
}

export type PanelLanguage = 'pt' | 'en' | 'es' | 'fr';

export function fetchPreferences(): Promise<ApiResult<{ language: PanelLanguage | null }>> {
  return api.get<{ language: PanelLanguage | null }>('api/v2/account/preferences');
}

export function savePreferences(
  language: PanelLanguage,
): Promise<ApiResult<{ language: PanelLanguage }>> {
  return api.put<{ language: PanelLanguage }>('api/v2/account/preferences', { language });
}
